import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useCommandPalette } from '@/components/command/useCommandPalette'
import { useOptionalFirstRun } from '@/components/first-run/FirstRunProvider'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { tutorialForPath, tutorialLength } from '@/components/tutorials/registry'
import type { TutorialDef } from '@/components/tutorials/types'
import { TutorialPlayer } from '@/components/tutorials/TutorialPlayer'
import { useTutorialProgress } from '@/components/tutorials/useTutorialProgress'
import s from './TutorialPeekTab.module.css'

/** How long a page settles before the tab rises out of the edge. */
const ENTER_DELAY_MS = 1200
/** Matches the CSS exit (translateX(100%) over 220ms, cubic-in). */
const EXIT_MS = 220

interface TutorialPeekTabProps {
  /** Help (or the phone nav drawer) is open: stand down. */
  suppressed?: boolean
}

/**
 * The per-page explanation-video peek tab (2026-09-23 entry in
 * plans/help-support-status.md; design system #help-peek-tab). On a
 * page that owns a tutorial the person hasn't watched or waved off, a small
 * tab rises out of the right edge after the page settles, looping a muted
 * mini cut of the film. Pressing it opens the video in Help (which counts as
 * watched); × ("Not now") retires it for this version of the film. It stays
 * out of the way while Help, the phone nav drawer, or the coach-mark tour is
 * up, and clear of an open decision pane (TutorialPeekTab.module.css). This
 * supersedes the design system's first-run rule "nothing fires on page load"
 * for this one surface, per Carson's 2026-09-23 direction
 * (plans/help-support-status.md).
 */
export function TutorialPeekTab({ suppressed = false }: TutorialPeekTabProps) {
  const { pathname } = useLocation()
  const { requestHelp } = useCommandPalette()
  const firstRun = useOptionalFirstRun()
  const progress = useTutorialProgress()
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')

  const tutorial = tutorialForPath(pathname)
  const target = tutorial && !suppressed && !firstRun?.tourActive && progress.isUnwatched(tutorial) ? tutorial : null
  const targetId = target?.id ?? null

  // What is on screen, and whether it is on its way out.
  const [shown, setShown] = useState<{ tutorial: TutorialDef; leaving: boolean } | null>(null)

  useEffect(() => {
    if (shown) {
      // A different page, Help opening, a watch or a dismissal: slide away.
      if (shown.tutorial.id !== targetId && !shown.leaving) setShown({ ...shown, leaving: true })
      return undefined
    }
    if (!target) return undefined
    const timer = window.setTimeout(() => setShown({ tutorial: target, leaving: false }), ENTER_DELAY_MS)
    return () => window.clearTimeout(timer)
    // `target` follows `targetId`; keying on the id keeps the delay from
    // restarting on unrelated renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetId, shown])

  useEffect(() => {
    if (!shown?.leaving) return undefined
    const timer = window.setTimeout(() => setShown(null), reduced ? 0 : EXIT_MS)
    return () => window.clearTimeout(timer)
  }, [shown, reduced])

  if (!shown) return null
  const def = shown.tutorial

  return (
    <aside
      className={s.peek}
      data-leaving={shown.leaving ? 'true' : undefined}
      aria-label="Explanation video for this page"
    >
      {/* Named by its visible text (the mini film is aria-hidden), so a
          speech-input user can say what they see. */}
      <button
        type="button"
        className={s.main}
        onClick={() => requestHelp({ view: 'tutorial', tutorialId: def.id })}
        onPointerEnter={() => void def.preload?.()}
        tabIndex={shown.leaving ? -1 : undefined}
      >
        <span className={s.head}>
          <span className={s.dot} aria-hidden="true" />
          <span className={s.label}>Click here for a quick explanation video</span>
        </span>
        <span className={s.player}>
          <TutorialPlayer tutorial={def} variant="mini" autoPlay loop />
        </span>
        <span className={s.caption}>
          {def.title} · {tutorialLength(def)}
        </span>
      </button>
      <button
        type="button"
        className={s.close}
        aria-label="Not now"
        title="Not now"
        tabIndex={shown.leaving ? -1 : undefined}
        onClick={(event) => {
          event.stopPropagation()
          // The tab is about to go; keep keyboard focus off <body>.
          const hadFocus = event.currentTarget.closest('aside')?.contains(document.activeElement)
          progress.dismissPeek(def)
          if (hadFocus) document.querySelector<HTMLElement>('[data-help-trigger]')?.focus()
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
          <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
        </svg>
      </button>
    </aside>
  )
}
